import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getUserInfo } from "../utils/AuthUtils";
import { parseQueryParams } from "../utils/CommonUtils";
import {
  getAccessibleProducts,
  hasProductAccess,
  hasJurisdictions,
  canReturnToPortal,
} from "../utils/permissionUtils";

const UserContext = createContext({
  userInfo: null,
  accessibleProducts: [],
  canAccess: () => false,
  hasJurisdictions: false,
  canReturnToPortal: false,
});

export const UserProvider = ({ children }) => {
  // Consume the `?info` blob exactly ONCE on mount: persist + decode it so
  // permissions are available immediately, then hold userInfo in state. We do NOT
  // re-derive userInfo from the URL afterwards — otherwise stripping `info` from
  // the URL (below) would re-run resolution.
  const [userInfo] = useState(() => {
    const infoData = new URLSearchParams(window.location.search).get("info");
    const parsed = infoData ? parseQueryParams(infoData) : null;
    return parsed?.userInfo || getUserInfo();
  });

  // Strip the `info` blob from the URL after it's been consumed, so the payload
  // doesn't linger in the address bar, history, or shareable links. Uses the
  // native History API (NOT react-router navigation) so it only rewrites the
  // address bar without issuing a route change — otherwise it would race with and
  // clobber permission-based route redirects (e.g. ProtectedProduct's <Navigate>).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("info")) return;
    params.delete("info");
    const query = params.toString();
    const newUrl =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", newUrl);
  }, []);

  const value = useMemo(() => {
    return {
      userInfo,
      accessibleProducts: getAccessibleProducts(userInfo),
      canAccess: (productKey) => hasProductAccess(productKey, userInfo),
      hasJurisdictions: hasJurisdictions(userInfo),
      canReturnToPortal: canReturnToPortal(userInfo),
    };
  }, [userInfo]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
